import useRequest from "api/useRequest";
import { FC, useRef } from "react";
import {
    Button,
    Loader,
    LoadingButton,
    Modal,
    ModalActions,
    ModalContent,
    ModalTitle,
    RequestMessage,
    Settings,
    SettingsItem,
} from "ui";
import { providersGet, providersUpdate } from "../actions";
import { AxiosResponse } from "axios";
import { Box } from "@mui/material";
import GastroPayForm, {
    GastroPayModel,
} from "../forms/GastroPay/GastroPayForm";
import AlexSushiInvoiceForm, {
    AlexSushiInvoiceModel,
} from "../forms/AlexSushiInvoice/AlexSushiInvoiceForm";
import DatatransUpdateForm from "../forms/Datatrans/DatatransUpdateForm";
import DatatransModel from "../forms/Datatrans/types/DatatransModel";
import BamboraModel from "../forms/Bambora/types/BamboraModel";
import BamboraUpdateForm from "../forms/Bambora/BamboraUpdateForm";
import DinteroUpdateForm from "../forms/Dintero/DinteroUpdateForm";
import DinteroModel from "../forms/Dintero/types/DinteroModel";
import ElavonUpdateForm from "../forms/Elavon/ElavonUpdateForm";
import NetsModel from "../forms/Nets/types/NetsModel";
import NetsUpdateForm from "../forms/Nets/NetsUpdateForm";
import StripeModel from "../forms/Stripe/types/StripeModel";
import StripeUpdateForm from "../forms/Stripe/StripeUpdateForm";
import VippsModel from "../forms/Vipps/types/VippsModel";
import VippsUpdateForm from "../forms/Vipps/VippsUpdateForm";

type ProviderUpdateModalProps = {
    id: number;
    project: string;
    prefix: string;
    onCloseModal: () => void;
};

const ProviderUpdateModal: FC<ProviderUpdateModalProps> = ({
    id,
    onCloseModal,
    project,
    prefix,
}) => {
    const formRef = useRef<any>();

    const { currentData, isPending } = useRequest(
        providersGet(project, prefix, id),
    );

    const { isLoading, request, errors, message, status } = useRequest();

    const handleClose = () => {
        onCloseModal();
    };

    const handleSaveForm = (
        event: React.FormEvent<HTMLFormElement | HTMLButtonElement>,
    ) => {
        formRef.current?.onSubmit(event, true);
    };

    const handleSubmit = (
        data:
            | AlexSushiInvoiceModel
            | BamboraModel
            | DatatransModel
            | DinteroModel
            | GastroPayModel
            | NetsModel
            | StripeModel
            | VippsModel,
    ) => {
        request(
            providersUpdate(
                project,
                prefix,
                id,
                currentData.data.provider,
                data,
                (r: AxiosResponse) => {
                    if (r.status === 200) {
                        onCloseModal();
                    }
                },
            ),
        );
    };

    const renderPaymentForm = () => {
        if (!currentData.data.provider) return null;

        switch (currentData.data.provider) {
            case "alex_sushi_invoice":
                return (
                    <AlexSushiInvoiceForm
                        ref={formRef}
                        errors={errors}
                        loading={isLoading}
                        onSubmit={handleSubmit}
                        data={currentData.data.settings}
                    />
                );
            case "bambora":
                return (
                    <BamboraUpdateForm
                        ref={formRef}
                        errors={errors}
                        loading={isLoading}
                        onSubmit={handleSubmit}
                        data={currentData.data.settings}
                    />
                );

            case "datatrans":
                return (
                    <DatatransUpdateForm
                        ref={formRef}
                        errors={errors}
                        loading={isLoading}
                        onSubmit={handleSubmit}
                        data={currentData.data.settings}
                    />
                );
            case "dintero":
                return (
                    <DinteroUpdateForm
                        ref={formRef}
                        errors={errors}
                        loading={isLoading}
                        onSubmit={handleSubmit}
                        data={currentData.data.settings}
                    />
                );
            case "elavon_converge":
                return (
                    <ElavonUpdateForm
                        ref={formRef}
                        errors={errors}
                        loading={isLoading}
                        onSubmit={handleSubmit}
                        data={{
                            ...currentData.data.settings,
                            accounts: currentData.data.accounts,
                        }}
                    />
                );
            case "gastropay":
                return (
                    <GastroPayForm
                        ref={formRef}
                        errors={errors}
                        loading={isLoading}
                        onSubmit={handleSubmit}
                        data={{
                            ...currentData.data.settings.settings,
                            commissions: currentData.data.settings.commissions,
                            sellers: currentData.data.settings.sellers,
                        }}
                    />
                );

            case "nets_easy":
                return (
                    <NetsUpdateForm
                        ref={formRef}
                        errors={errors}
                        loading={isLoading}
                        onSubmit={handleSubmit}
                        data={currentData.data.settings}
                    />
                );

            case "stripe":
                return (
                    <StripeUpdateForm
                        ref={formRef}
                        errors={errors}
                        loading={isLoading}
                        onSubmit={handleSubmit}
                        data={currentData.data.settings}
                    />
                );
            case "vipps":
                return (
                    <VippsUpdateForm
                        ref={formRef}
                        errors={errors}
                        loading={isLoading}
                        onSubmit={handleSubmit}
                        data={currentData.data.settings}
                    />
                );
        }
    };

    if (isPending || !currentData.data) return <Loader />;

    return (
        <Modal
            open
            maxWidth="lg"
            fullWidth
            PaperProps={{ sx: { position: "fixed", top: 0 } }}
        >
            <ModalTitle onClose={() => onCloseModal()}>
                Update payment provider
            </ModalTitle>
            <ModalContent sx={{ pt: 0, px: 2 }}>
                <RequestMessage
                    message={message}
                    status={status}
                    sx={{
                        mb: 1,
                    }}
                />

                <Settings>
                    <SettingsItem label="UUID">
                        <pre
                            style={{
                                margin: 0,
                            }}
                        >
                            {currentData.data.uuid}
                        </pre>
                    </SettingsItem>
                </Settings>
                {renderPaymentForm()}
            </ModalContent>
            <ModalActions>
                <Box display="flex" width="100%" textAlign="right">
                    <Button
                        onClick={handleClose}
                        color="inherit"
                        variant="text"
                        sx={{ mr: 1 }}
                    >
                        Cancel
                    </Button>
                    <LoadingButton
                        sx={{ flex: 1 }}
                        onClick={handleSaveForm}
                        loading={isLoading}
                    >
                        Save
                    </LoadingButton>
                </Box>
            </ModalActions>
        </Modal>
    );
};

export default ProviderUpdateModal;
